import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { listAgents, updateAgent, type Agent } from './api';

type OrchestratorConfig = {
  delegates?: string[];
  max_delegate_calls?: number;
};

export function OrchestratorConfigForm({ orgId, agent }: { orgId: string; agent: Agent }) {
  const qc = useQueryClient();
  const cfg = (agent.config as OrchestratorConfig) ?? {};

  const candidates = useQuery({
    queryKey: ['agents', orgId],
    queryFn: () => listAgents(orgId),
    enabled: !!orgId,
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [maxCalls, setMaxCalls] = useState<string>('10');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSelected(new Set(cfg.delegates ?? []));
    setMaxCalls(cfg.max_delegate_calls != null ? String(cfg.max_delegate_calls) : '10');
    setSaved(false);
  }, [agent.id]);

  // The orchestrator can't delegate to itself, can't delegate to other
  // orchestrators (avoids "manager-of-managers" trees of unclear value
  // in V1), and we hide archived agents.
  const choices = (candidates.data ?? [])
    .filter((a) => a.id !== agent.id)
    .filter((a) => a.status !== 'archived')
    .filter((a) => a.template_slug !== 'orchestrator');

  const save = useMutation({
    mutationFn: () => {
      const next: OrchestratorConfig = {
        delegates: Array.from(selected),
        max_delegate_calls: Number(maxCalls) > 0 ? Number(maxCalls) : 0,
      };
      return updateAgent(orgId, agent.id, { config: next });
    },
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['agent', orgId, agent.id] });
      setTimeout(() => setSaved(false), 3000);
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Card>
      <CardHeader
        title="Orchestration policy"
        description="The orchestrator may only call agents you whitelist below. The cap limits per-run delegations to keep token cost predictable."
      />
      <CardBody>
        <form onSubmit={handleSubmit} className="space-y-5">
          <Section
            title="Delegates"
            hint="Sigil agents this orchestrator may invoke via call_agent. Leave empty for a read-only orchestrator (it can still use its connectors but won't move funds)."
          >
            {candidates.isLoading && (
              <p className="text-sm text-ink-tertiary">Loading agents…</p>
            )}
            {!candidates.isLoading && choices.length === 0 && (
              <p className="text-sm text-ink-tertiary">
                No other agents in this organisation yet.{' '}
                <Link to="/agents/new" className="text-bit2me hover:underline">
                  Create one →
                </Link>
              </p>
            )}
            <ul className="space-y-1.5">
              {choices.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-line-soft bg-surface-white px-3 py-2"
                >
                  <label className="flex flex-1 cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggle(c.id)}
                      className="mt-1 accent-bit2me"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink-primary">{c.name}</p>
                      <p className="font-mono text-[10px] uppercase text-ink-tertiary">
                        {c.template_slug}
                      </p>
                      {c.description && (
                        <p className="mt-0.5 text-xs text-ink-secondary">{c.description}</p>
                      )}
                    </div>
                  </label>
                </li>
              ))}
            </ul>
          </Section>

          <Section
            title="Max delegate calls per run"
            hint="Hard cap on call_agent invocations. Prevents runaway loops; the LLM is told to summarise instead of squeezing in one more delegation when close to the cap."
          >
            <input
              type="number"
              min={0}
              step={1}
              value={maxCalls}
              onChange={(e) => setMaxCalls(e.target.value)}
              className="mt-1 block w-32 rounded-md border border-line bg-surface-white px-2 py-1.5 text-sm text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
            />
          </Section>

          {save.error && (
            <p className="text-sm text-danger">{(save.error as Error).message || 'Could not save'}</p>
          )}
          <div className="flex items-center justify-end gap-3">
            {saved && <p className="font-mono text-xs text-ok">Saved</p>}
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink-primary">{title}</label>
      {hint && <p className="mt-0.5 text-xs text-ink-tertiary">{hint}</p>}
      <div className="mt-2">{children}</div>
    </div>
  );
}
